import { WORKFLOW_CANVAS as C } from "@sagot/constants";
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent, type PointerEvent, type RefObject } from "react";
import { clampZoom, type Box, type Point } from "./canvas-layout";
import { prefersReducedMotion } from "./motion";

interface Grab {
  pointer: Point;
  scroll: Point;
}

export interface PanZoom {
  viewport: RefObject<HTMLDivElement | null>;
  zoom: number;
  panning: boolean;
  zoomIn: () => void;
  zoomOut: () => void;
  resetZoom: () => void;
  scrollToStart: () => void;
  /** Scrolls so the box (a tile, in canvas units) sits in the middle of the viewport; smoothly unless motion is reduced. */
  scrollToBox: (box: Box) => void;
  /** Spread on the scrolling viewport: drag the background to pan, arrow keys pan, `+` / `-` / `0` zoom. */
  handlers: {
    onPointerDown: (event: PointerEvent<HTMLDivElement>) => void;
    onPointerMove: (event: PointerEvent<HTMLDivElement>) => void;
    onPointerUp: (event: PointerEvent<HTMLDivElement>) => void;
    onPointerCancel: (event: PointerEvent<HTMLDivElement>) => void;
    onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => void;
  };
}

/** Elements that carry this attribute (`data-pan`: the canvas surface) start a pan when dragged; tiles and buttons do not. */
const PAN_HANDLE = "data-pan";

const PAN_KEYS: Readonly<Record<string, Point>> = {
  ArrowLeft: { x: -C.panStep, y: 0 },
  ArrowRight: { x: C.panStep, y: 0 },
  ArrowUp: { x: 0, y: -C.panStep },
  ArrowDown: { x: 0, y: C.panStep },
};

/**
 * Pan and zoom for the canvas. Panning is native scrolling (wheel, trackpad, scrollbars) plus dragging the background;
 * zoom scales the content from the top left and keeps the point under the pointer (or the centre) in place. Ctrl or
 * Cmd with the wheel zooms, as pinch does on a trackpad.
 */
export function usePanZoom(): PanZoom {
  const viewport = useRef<HTMLDivElement | null>(null);
  const zoomRef = useRef<number>(C.zoomDefault);
  const pendingScroll = useRef<Point | null>(null);
  const grab = useRef<Grab | null>(null);
  const [zoom, setZoom] = useState<number>(C.zoomDefault);
  const [panning, setPanning] = useState(false);

  const zoomTo = useCallback((next: number, anchor?: Point) => {
    const element = viewport.current;
    const previous = zoomRef.current;
    const value = clampZoom(next);
    if (value === previous) return;
    if (element) {
      const at = anchor ?? { x: element.clientWidth / 2, y: element.clientHeight / 2 };
      pendingScroll.current = { x: ((element.scrollLeft + at.x) / previous) * value - at.x, y: ((element.scrollTop + at.y) / previous) * value - at.y };
    }
    zoomRef.current = value;
    setZoom(value);
  }, []);

  // The content grows or shrinks with the zoom: scroll after it has, so the anchor stays under the pointer.
  useLayoutEffect(() => {
    const element = viewport.current;
    const target = pendingScroll.current;
    if (!element || !target) return;
    element.scrollLeft = target.x;
    element.scrollTop = target.y;
    pendingScroll.current = null;
  }, [zoom]);

  // React registers wheel listeners as passive, which cannot stop the browser zooming the whole page: attach one by hand.
  useEffect(() => {
    const element = viewport.current;
    if (!element) return undefined;
    const onWheel = (event: WheelEvent) => {
      if (!event.ctrlKey && !event.metaKey) return;
      event.preventDefault();
      const box = element.getBoundingClientRect();
      zoomTo(zoomRef.current + (event.deltaY < 0 ? C.zoomStep : -C.zoomStep), { x: event.clientX - box.left, y: event.clientY - box.top });
    };
    element.addEventListener("wheel", onWheel, { passive: false });
    return () => { element.removeEventListener("wheel", onWheel); };
  }, [zoomTo]);

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    const element = viewport.current;
    const onBackground = event.target === event.currentTarget || (event.target instanceof Element && event.target.hasAttribute(PAN_HANDLE));
    if (!element || event.button !== 0 || !onBackground) return;
    grab.current = { pointer: { x: event.clientX, y: event.clientY }, scroll: { x: element.scrollLeft, y: element.scrollTop } };
    element.setPointerCapture(event.pointerId);
    setPanning(true);
  };
  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const element = viewport.current;
    const start = grab.current;
    if (!element || !start) return;
    element.scrollLeft = start.scroll.x - (event.clientX - start.pointer.x);
    element.scrollTop = start.scroll.y - (event.clientY - start.pointer.y);
  };
  const release = (event: PointerEvent<HTMLDivElement>) => {
    if (!grab.current) return;
    grab.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    setPanning(false);
  };
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const element = viewport.current;
    if (!element || event.target !== event.currentTarget) return;
    const pan = PAN_KEYS[event.key];
    if (pan) {
      event.preventDefault();
      element.scrollBy({ left: pan.x, top: pan.y });
    } else if (event.key === "+" || event.key === "=") {
      event.preventDefault();
      zoomTo(zoomRef.current + C.zoomStep);
    } else if (event.key === "-") {
      event.preventDefault();
      zoomTo(zoomRef.current - C.zoomStep);
    } else if (event.key === "0") {
      event.preventDefault();
      zoomTo(C.zoomDefault);
    }
  };

  return {
    viewport,
    zoom,
    panning,
    zoomIn: () => { zoomTo(zoomRef.current + C.zoomStep); },
    zoomOut: () => { zoomTo(zoomRef.current - C.zoomStep); },
    resetZoom: () => { zoomTo(C.zoomDefault); },
    scrollToStart: () => { viewport.current?.scrollTo({ left: 0, top: 0 }); },
    scrollToBox: (box) => {
      const element = viewport.current;
      if (!element) return;
      const scale = zoomRef.current;
      const tall = box.height * scale > element.clientHeight;
      element.scrollTo({
        left: box.x * scale - (element.clientWidth - box.width * scale) / 2,
        top: tall ? box.y * scale - C.padding * scale : box.y * scale - (element.clientHeight - box.height * scale) / 2,
        behavior: prefersReducedMotion() ? "auto" : "smooth",
      });
    },
    handlers: { onPointerDown, onPointerMove, onPointerUp: release, onPointerCancel: release, onKeyDown },
  };
}
